import React, { useEffect, useState } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
  ComposedChart,
  Line
} from 'recharts';
import {
  BarChart3,
  PieChart as PieIcon,
  Activity,
  TrendingUp,
  DollarSign,
  Layers,
  AlertCircle,
  Loader2,
  Stethoscope
} from 'lucide-react';
import apiService from '../services/apiService';
import { SpecialtyAnalysis } from '../types/dashboard';
import { formatCurrency, formatNumber } from '../utils/formatters';

const CHART_PALETTE = {
  primary: '#2563eb',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  purple: '#8b5cf6',
  cyan: '#06b6d4',
  amber: '#d97706',
  indigo: '#4f46e5'
};

const PIE_COLORS = ['#2563eb', '#10b981', '#f59e0b', '#8b5cf6', '#06b6d4', '#ec4899'];

interface DiagnosisPoint {
  name: string;
  cases: number;
  totalCost: number;
  avgCost: number;
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-popover/95 backdrop-blur-md p-3 rounded-xl border border-border shadow-xl text-xs space-y-1 z-50">
        <p className="font-bold text-foreground mb-1.5 border-b border-border/60 pb-1">{label}</p>
        {payload.map((entry: any, index: number) => (
          <div key={`item-${index}`} className="flex items-center justify-between gap-4">
            <span className="flex items-center gap-1.5 text-muted-foreground">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color || entry.fill }} />
              {entry.name}:
            </span>
            <span className="font-bold font-mono text-foreground">
              {typeof entry.value === 'number' && (entry.name.toLowerCase().includes('cost') || entry.name.toLowerCase().includes('amount'))
                ? formatCurrency(entry.value)
                : formatNumber(entry.value)}
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const Analytics: React.FC = () => {
  const [specialties, setSpecialties] = useState<SpecialtyAnalysis[]>([]);
  const [diagnoses, setDiagnoses] = useState<DiagnosisPoint[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const [spec, diagsRaw] = await Promise.all([
          apiService.getSpecialtiesData(0),
          (async () => {
            const token = localStorage.getItem('fraudguard_token');
            const res = await fetch(`${window.location.origin}/api/dashboard/diagnoses?limit=0`, {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            });
            if (!res.ok) {
              throw new Error(`API Error: ${res.status} ${res.statusText}`);
            }
            return await res.json();
          })(),
        ]);
        const diags = (diagsRaw.diagnoses || []).map((d: any) => ({
          name: d.name,
          cases: d.cases,
          totalCost: d.totalCost,
          avgCost: d.avgCost,
        }));
        setSpecialties(spec);
        setDiagnoses(diags);
      } catch (e: any) {
        setError(e?.message || 'Failed to load analytics');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] p-12">
        <Loader2 className="w-10 h-10 text-primary-600 animate-spin mb-4" />
        <p className="text-xs font-semibold text-muted-foreground">Aggregating specialty and diagnosis trends...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 flex items-center gap-4">
        <AlertCircle className="w-6 h-6 text-rose-600 shrink-0" />
        <div>
          <h4 className="text-sm font-bold text-rose-900">Analytics Service Error</h4>
          <p className="text-xs text-rose-700 mt-0.5">{error}</p>
        </div>
      </div>
    );
  }

  // Calculate KPIs
  const totalClaims = specialties.reduce((sum, s) => sum + s.claims, 0);
  const totalAmount = specialties.reduce((sum, s) => sum + s.totalAmount, 0);
  const avgClaimAmount = totalClaims > 0 ? totalAmount / totalClaims : 0;
  const topSpecialty = specialties.length > 0 ? specialties[0] : null;

  // Chart data
  const top5Specialties = specialties.slice(0, 5);
  const top5Diagnoses = diagnoses.slice(0, 5);

  const costDistribution = specialties.slice(0, 4).map(s => ({
    name: s.name,
    value: s.totalAmount,
  }));
  const othersTotal = specialties.slice(4).reduce((sum, s) => sum + s.totalAmount, 0);
  if (othersTotal > 0) costDistribution.push({ name: 'Others', value: othersTotal });

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="glass-panel p-6 rounded-2xl border border-border shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-600 to-indigo-600 text-white flex items-center justify-center shadow-md shadow-primary-500/20">
            <BarChart3 className="w-6 h-6" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold text-foreground">Advanced Analytics & Diagnoses</h2>
              <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-primary-50 text-primary-700 border border-primary-200">
                Multi-Dimensional
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              Deep clinical diagnosis patterns, cost allocations, and volume distribution
            </p>
          </div>
        </div>
      </div>

      {/* KPI Cards Bento */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="glass-card p-4 rounded-2xl border border-border shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold text-muted-foreground tracking-wide">Claims Volume</span>
            <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center border border-blue-100">
              <Layers className="w-4 h-4" />
            </div>
          </div>
          <p className="text-2xl font-black font-mono text-foreground">{formatNumber(totalClaims)}</p>
          <p className="text-[11px] text-muted-foreground mt-1">Across all provider networks</p>
        </div>

        <div className="glass-card p-4 rounded-2xl border border-border shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold text-muted-foreground tracking-wide">Cumulative Invoiced</span>
            <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center border border-emerald-100">
              <DollarSign className="w-4 h-4" />
            </div>
          </div>
          <p className="text-2xl font-black font-mono text-emerald-600">{formatCurrency(totalAmount)}</p>
          <p className="text-[11px] text-muted-foreground mt-1">Gross medical billing</p>
        </div>

        <div className="glass-card p-4 rounded-2xl border border-border shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold text-muted-foreground tracking-wide">Mean Claim Cost</span>
            <div className="w-8 h-8 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center border border-purple-100">
              <TrendingUp className="w-4 h-4" />
            </div>
          </div>
          <p className="text-2xl font-black font-mono text-purple-600">{formatCurrency(avgClaimAmount)}</p>
          <p className="text-[11px] text-muted-foreground mt-1">Average per claim unit</p>
        </div>

        <div className="glass-card p-4 rounded-2xl border border-border shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold text-muted-foreground tracking-wide">Leading Specialty</span>
            <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center border border-amber-100">
              <Stethoscope className="w-4 h-4" />
            </div>
          </div>
          <p className="text-base font-bold text-foreground truncate" title={topSpecialty?.name || 'N/A'}>
            {topSpecialty?.name || 'N/A'}
          </p>
          <p className="text-[11px] text-amber-700 font-mono font-bold mt-1">
            {topSpecialty ? formatNumber(topSpecialty.claims) : 0} claims logged
          </p>
        </div>
      </div>

      {/* Row 1: Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top 5 Specialties Bar */}
        <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden flex flex-col">
          <div className="p-4 bg-muted/40 border-b border-border flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-primary-600" />
              <div>
                <h3 className="text-xs font-bold text-foreground">Top 5 Clinical Specialties</h3>
                <p className="text-[11px] text-muted-foreground">Ranked by total claims processed</p>
              </div>
            </div>
          </div>
          <div className="p-5 flex-1 min-h-[320px]">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={top5Specialties} layout="vertical" margin={{ top: 10, right: 30, left: 90, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="hsl(var(--border))" />
                <XAxis type="number" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
                <YAxis type="category" dataKey="name" width={85} tick={{ fontSize: 11, fill: 'hsl(var(--foreground))' }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="claims" name="Total Claims" fill={CHART_PALETTE.primary} radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Cost Distribution Pie */}
        <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden flex flex-col">
          <div className="p-4 bg-muted/40 border-b border-border flex items-center justify-between">
            <div className="flex items-center gap-2">
              <PieIcon className="w-4 h-4 text-emerald-600" />
              <div>
                <h3 className="text-xs font-bold text-foreground">Specialty Cost Distribution</h3>
                <p className="text-[11px] text-muted-foreground">Proportion of gross claim expenditure</p>
              </div>
            </div>
          </div>
          <div className="p-5 flex-1 min-h-[320px]">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={costDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={95}
                  dataKey="value"
                  strokeWidth={2}
                  stroke="hsl(var(--card))"
                >
                  {costDistribution.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Row 2: Diagnoses & Cost Composed Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top 5 Diagnoses */}
        <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
          <div className="p-4 bg-muted/40 border-b border-border flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-purple-600" />
              <div>
                <h3 className="text-xs font-bold text-foreground">Top 5 Primary Diagnoses</h3>
                <p className="text-[11px] text-muted-foreground">Volume count vs average claim cost</p>
              </div>
            </div>
          </div>
          <div className="p-5">
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={top5Diagnoses} margin={{ top: 15, right: 20, bottom: 40, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="name" angle={-25} textAnchor="end" interval={0} height={60} tick={{ fontSize: 10, fill: 'hsl(var(--foreground))' }} />
                <YAxis yAxisId="left" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '10px' }} />
                <Bar yAxisId="left" dataKey="cases" name="Cases Count" fill={CHART_PALETTE.purple} radius={[6, 6, 0, 0]} />
                <Line yAxisId="right" type="monotone" dataKey="avgCost" name="Avg Cost" stroke={CHART_PALETTE.warning} strokeWidth={2.5} dot={{ r: 4 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Specialty Cost Analysis */}
        <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
          <div className="p-4 bg-muted/40 border-b border-border flex items-center justify-between">
            <div className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-amber-600" />
              <div>
                <h3 className="text-xs font-bold text-foreground">Specialty Cost & Volume Matrix</h3>
                <p className="text-[11px] text-muted-foreground">Total expenditure vs average claim size</p>
              </div>
            </div>
          </div>
          <div className="p-5">
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={top5Specialties} margin={{ top: 15, right: 20, bottom: 40, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="name" angle={-25} textAnchor="end" interval={0} height={60} tick={{ fontSize: 10, fill: 'hsl(var(--foreground))' }} />
                <YAxis yAxisId="left" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '10px' }} />
                <Bar yAxisId="left" dataKey="totalAmount" name="Total Invoiced" fill={CHART_PALETTE.success} radius={[6, 6, 0, 0]} />
                <Line yAxisId="right" type="monotone" dataKey="avgAmount" name="Average Claim" stroke={CHART_PALETTE.danger} strokeWidth={2.5} dot={{ r: 4 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;